<script setup lang="ts">
import { ALIAS_MAX, ALIAS_MIN, ALIAS_RENAME_COOLDOWN_MS, aliasProblem, normalizeAlias } from '../../shared/aliasFilter'
import type { AliasError } from '~/composables/useProfile'

/**
 * 👤 "Choose your name" (v6.43). Opens once per session for a signed-in
 * account with no alias, and from the account chip to rename. The same
 * filter the server applies gives live hints while typing; the server
 * has the last word (uniqueness, cooldown). "Later" is always allowed:
 * the boards then show Player-#### until a name is picked.
 */
const profile = useProfile()
const { t } = useLocale()
const sfx = useSfx()

const draft = ref('')
const busy = ref(false)
const serverError = ref<AliasError | null>(null)

const problem = computed(() => (draft.value.trim() ? aliasProblem(draft.value) : null))
const canSave = computed(() => !busy.value && normalizeAlias(draft.value).length >= ALIAS_MIN && problem.value === null)
const errorKey = computed(() => serverError.value ?? problem.value)
const renaming = computed(() => !!profile.alias.value)

watch(() => profile.pickerOpen.value, (open) => {
  if (open) {
    draft.value = profile.alias.value ?? ''
    serverError.value = null
  }
})
watch(draft, () => { serverError.value = null })

async function onSave() {
  if (!canSave.value) return
  sfx.confirm()
  busy.value = true
  serverError.value = await profile.save(draft.value)
  busy.value = false
}
function onLater() {
  sfx.tap()
  profile.dismiss()
}
// 📱 M6: Android's back answers Later (useBackStack) - never while a save is on its way.
useBackLayer('alias-picker', () => profile.pickerOpen.value, () => {
  if (busy.value) return false
  onLater()
})
</script>

<template>
  <div v-if="profile.pickerOpen.value" class="alias-backdrop" data-testid="alias-picker">
    <form class="card alias-card" @submit.prevent="onSave">
      <h2 class="alias-title">{{ t(renaming ? 'alias.titleChange' : 'alias.title') }}</h2>
      <p class="alias-note">{{ t('alias.body') }}</p>
      <p id="alias-rules" class="alias-hint" data-testid="alias-rules">
        {{ t('alias.rules', { min: ALIAS_MIN, max: ALIAS_MAX, days: ALIAS_RENAME_COOLDOWN_MS / 86_400_000 }) }}
      </p>
      <input
        v-model="draft"
        class="alias-input"
        type="text"
        inputmode="text"
        enterkeyhint="done"
        :maxlength="ALIAS_MAX + 4"
        autocomplete="nickname"
        autocapitalize="words"
        spellcheck="false"
        :placeholder="t('alias.placeholder')"
        aria-describedby="alias-rules"
        data-testid="alias-input"
      >
      <p v-if="errorKey" class="alias-hint is-error" role="alert">
        {{ t(`alias.err.${errorKey}`) }}
      </p>
      <div class="alias-actions">
        <button type="button" class="btn alias-later" @click="onLater">{{ t('alias.later') }}</button>
        <button type="submit" class="btn" :disabled="!canSave" data-testid="alias-save">{{ t('alias.save') }}</button>
      </div>
    </form>
  </div>
</template>

<style scoped>
.alias-backdrop {
  position: fixed;
  inset: 0;
  z-index: 80;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(12, 26, 48, 0.72);
  backdrop-filter: blur(2px);
  touch-action: none;
}
.alias-card {
  width: min(380px, 100%);
  /* Scroll inside the card when the keyboard shortens the viewport;
   * the backdrop keeps its gesture lock. */
  max-height: 100%;
  overflow-y: auto;
  overscroll-behavior: contain;
  touch-action: pan-y;
  display: flex;
  flex-direction: column;
  gap: 10px;
  text-align: center;
}
.alias-card > * {
  flex-shrink: 0;
}
.alias-title {
  margin: 0;
  font-size: 1.2rem;
}
.alias-note {
  margin: 0;
  font-size: 0.82rem;
  color: var(--color-ink-soft);
}
.alias-input {
  font: inherit;
  font-weight: 700;
  font-size: 1.05rem;
  text-align: center;
  padding: 10px 12px;
  border: 1.5px solid rgba(31, 58, 95, 0.35);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.9);
  color: var(--color-ink);
}
.alias-input:focus {
  outline: none;
  border-color: var(--color-gold-deep);
}
.alias-hint {
  margin: 0;
  min-height: 1.2em;
  font-size: 0.75rem;
  color: var(--color-ink-soft);
}
.alias-hint.is-error {
  color: #b23a3a;
  font-weight: 700;
}
.alias-actions {
  display: flex;
  gap: 8px;
  justify-content: center;
}
.alias-later {
  opacity: 0.85;
}
</style>
